import { FC, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/store/hooks";
import {
  closeErrorModal,
  closeToaster,
  setComplete,
  setError,
  setProcessing,
  startUpload,
  updateProgress,
  UploadSummary,
} from "src/store/slices/upload-progress.slice";
import AssetErrorModal from "./asset-error-modal.component";
import { downloadValidationReport } from "src/store/slices/asset.slice";
import { toast } from "react-toastify";
import { RetentionPolicyEnum } from "src/core/enums/asset-enums";

interface GlobalAssetErrorModalProps {
  onRefetchData?: () => void;
}

const GlobalAssetErrorModal: FC<GlobalAssetErrorModalProps> = ({
  onRefetchData,
}) => {
  const dispatch = useAppDispatch();
  const [retentionPolicy, setRetentionPolicy] = useState<RetentionPolicyEnum>(
    RetentionPolicyEnum.RETAIN_ASSETS_NOT_IN_FILE,
  );

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {
    errorModalOpen,
    fileUploadResponse,
    editableRows,
    infoRows,
    modalNumberOfRecords,
    jobId,
    uploadFile,
    summary,
    toBeDeletedCount,
    phase,
    uploadMode
  } = useSelector((state: any) => state.uploadProgress);

  useEffect(() => {
    if (phase === "uploading") {
      setRetentionPolicy(RetentionPolicyEnum.RETAIN_ASSETS_NOT_IN_FILE);
    }
  }, [phase]);

  const selectedItems = useSelector((state: any) => state.table.selectedItems);

  const showToast = (
    type: "success" | "warning" | "error",
    message: string,
  ) => {
    toast[type](message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnFocusLoss: true,
      draggable: true,
      pauseOnHover: true,
    });
  };

  const buildUploadFormData = (
    uploadFile: File,
    isValidation: boolean,
    selectedDeviceCodes: any[] = [],
  ) => {
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("isValidation", isValidation ? "true" : "false");
    formData.append("retentionPolicy", retentionPolicy.toString());
    formData.append("confirmed", "true");
    formData.append("uploadMode", uploadMode || "");

    for (let item of selectedDeviceCodes) {
      formData.append("deviceCodesForUpdate[]", item.value);
    }
    return formData;
  };

  const pollJobStatus = async (jobId: string, dispatch: any): Promise<any> => {
    const maxAttempts = 240;

    // Dynamic import to avoid circular dependency
    const { getAssetUploadStatus } =
      await import("src/store/slices/asset.slice");

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise<void>((resolve) => setTimeout(resolve, 5000));

      try {
        const response: any = await dispatch(getAssetUploadStatus({ jobId }));
        const data = response?.payload;

        if (data?.status === "Completed") return data?.result;
        if (data?.status === "Failed") {
          console.error("Asset upload job failed:", data?.error);
          showToast("error", data?.error ?? "Asset upload failed.");
          return null;
        }
        if (data?.status === "Processing") {
          const progress = data?.progress ?? 0;
          dispatch(updateProgress(progress));
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }

    showToast("error", "Asset upload timed out. Please try again.");
    return null;
  };

  const updateUploadProgress = (progressEvent: any) => {
    const { loaded, total } = progressEvent;
    if (total !== null && total !== undefined) {
      const percentCompleted = Math.round((loaded * 100) / total);
      dispatch(updateProgress(percentCompleted));
    }
  };

  const uploadAndPoll = async (formData: FormData) => {
    try {
      // Dynamic import to avoid circular dependency
      const { uploadAssetFile } = await import("src/store/slices/asset.slice");

      const uploadResponse: any = await dispatch(
        uploadAssetFile({
          formData,
          onUploadProgress: updateUploadProgress
        }),
      );

      const uploadJobId =
        uploadResponse?.payload?.jobId ?? uploadResponse?.payload?.data?.jobId;
      if (!uploadJobId) {
        dispatch(updateProgress(0));
        return null;
      }
      dispatch(setProcessing({ jobId: uploadJobId }));

      return await pollJobStatus(uploadJobId, dispatch);
    } catch (error) {
      console.error("Error during asset upload:", error);
      return null;
    }
  };

  const confirmAssetsFromErrorResults = useCallback(async () => {
    if (!uploadFile) {
      showToast("error", "Upload file not found. Please try again.");
      return;
    }

    dispatch(startUpload({ fileName: uploadFile.name, file: uploadFile }));


    try {
      const selectedItemsData = selectedItems.filter((item: any) => item.value);
      const formData = buildUploadFormData(
        uploadFile,
        false,
        selectedItemsData
      );

      const result = await uploadAndPoll(formData);
      if (!result) return;

      if ([200, 201].includes(result.status)) {
        showToast("success", result.message);
      } else if (result.message) {
        showToast("warning", result.message);
      }

      dispatch(closeErrorModal());

      if (onRefetchData) {
        onRefetchData();
      }

      if (result?.status === 400) {
        dispatch(setError());
      } else {
        const updatedSummary: UploadSummary = {
          ...(summary || {
            added: 0,
            total: 0,
            errors: 0,
            message: "",
            hasValidationErrors: false,
            resultStatus: [200, 201].includes(result.status),
          }),
          message: result.message ?? "",
          hasValidationErrors: false,
          resultStatus: [200, 201].includes(result.status),
        };

        dispatch(
          setComplete({
            summary: updatedSummary,
            insertedAssetCodes: result?.data?.insertedAssetCodes ?? [],
            deletedAssetCodes: result?.data?.deletedAssetCodes ?? [],
          }),
        );
      }
    } catch (error) {
      dispatch(updateProgress(0));
      dispatch(setError());
      console.error("Error uploading file:", error);
    }
  }, [uploadFile, selectedItems, dispatch, onRefetchData, summary, retentionPolicy, buildUploadFormData]);

  const handleCloseModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false)
  }

  const handleAbortUpload = () => {
    setShowConfirmationModal(false);
    dispatch(closeErrorModal());
    dispatch(closeToaster());

    if (onRefetchData) {
      onRefetchData();
    }
  }

  const modalActionConfig: any = [
    {
      label: "Abort Upload",
      event: handleAbortUpload,
      variant: "primary",
    },
  ]

  const handleDownloadValidationReport = (jobId: string | number) => {
    dispatch(downloadValidationReport(jobId));
  };

  useEffect(() => {
    return () => {
      dispatch(closeErrorModal());
    }
  }, [])

  return (
    <AssetErrorModal
      isAssetModalOpen={errorModalOpen}
      handleCloseModal={handleCloseModal}
      uploadJobId={jobId}
      onDownloadValidationReport={handleDownloadValidationReport}
      selectedItems={selectedItems}
      onConfirmAssetsFromErrorResults={confirmAssetsFromErrorResults}
      fileUploadResponse={fileUploadResponse}
      modalNumberOfRecords={modalNumberOfRecords}
      editableRows={editableRows}
      infoRows={infoRows}
      retentionPolicy={retentionPolicy}
      setRetentionPolicy={setRetentionPolicy}
      showConfirmationModal={showConfirmationModal}
      handleCloseConfirmationModal={handleCloseConfirmationModal}
      modalActionConfig={modalActionConfig}
      toBeDeletedCount={toBeDeletedCount}
      phase={phase}
    />

  );
};

export default GlobalAssetErrorModal;
